import React, { useState, useCallback, useEffect } from "react"
import { navigate } from "vike/client/router"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { FeatureLockAPI } from "aldoo-ra/FeatureLock/api"
import DataTable from "aldoo-ra/DataTable"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Modal from "aldoo-ra/Modal"
import Checkbox from "aldoo-ra/Checkbox"
import FileShareController from "../FileShareFeature/FileShareController"
import * as ShareAPI from "../FileShareFeature/api"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import Tooltip from "aldoo-ra/Tooltip"
import {
  ShareIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  PlusIcon,
  CalendarDaysIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"
import { showToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import config from "../../config"
import {
  STATUS_OPTIONS,
  formatBytes,
  formatNumber,
  formatRelativeTime,
  formatTimeRemaining,
} from "./utils"
import StatusBadge from "./StatusBadge"
import ShareDrawer from "./ShareDrawer"
import DeleteModal from "./DeleteModal"
import MobileFileList from "./MobileFileList"
import ExtendModal from "./ExtendModal"
import DownloadLimitModal from "./DownloadLimitModal"
import { openGoPremium } from "../GoPremiumDialog"

const SHARE_EXTEND_FEATURE_ID = "share-extend"

export default function FileShareDashboard() {
  const [auth] = PlatformAuth.useAuth()
  const texts = TextBundle({ id: "transfer_share_dashboard" })

  const [hasExtend, setHasExtend] = useState(false)

  const loadFeatures = useCallback(() => {
    FeatureLockAPI.hasFeature(SHARE_EXTEND_FEATURE_ID)
      .then((has) => setHasExtend(!!has))
      .catch(() => {})
  }, [])
  useEffect(() => {
    loadFeatures()
    const t1 = PubSub.subscribe("payment-success", () => loadFeatures())
    return () => { PubSub.unsubscribe(t1) }
  }, [loadFeatures])

  // Extend is Pro-only. Non-Pro users get the Go Premium upsell. Pro users who
  // have exhausted the lifetime extend cap are told to create a new share.
  const openExtend = useCallback((share) => {
    if (!hasExtend) { openGoPremium(); return }
    if ((share?.extends_left ?? 3) <= 0) {
      showMessageDialog({
        title: texts?.extendLimitTitle,
        message: texts?.extendLimitMessage,
      })
      return
    }
    setExtendModal({ share })
  }, [hasExtend, texts])

  const [selected, setSelected] = useState(new Set())
  const [currentData, setCurrentData] = useState([])
  const [copiedToken, setCopiedToken] = useState(null)
  const [drawerShare, setDrawerShare] = useState(null)
  const [extendModal, setExtendModal] = useState(null)
  const [extending, setExtending] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [statusFilter, setStatusFilter] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [limitModal, setLimitModal] = useState(false)
  const [limitValue, setLimitValue] = useState("")
  const [limitSaving, setLimitSaving] = useState(false)
  const [showNewShare, setShowNewShare] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  // Cached plan quota — drives the count-based pre-flight on the "New
  // Share" CTA so a free user who's spent their monthly transfers sees
  // the GoPremium dialog on the very click, not after picking files.
  const [quota, setQuota] = useState(null)

  useEffect(() => {
    let cancelled = false
    ShareAPI.GetQuota().then((q) => { if (!cancelled && q?.result) setQuota(q) }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  const openNewShare = () => {
    // Entry-button gate: only count-based limits make sense here (file
    // size / volume need actual files; retention needs a selection).
    if (Number.isFinite(quota?.countLimit) && quota.countLimit > 0
        && Number.isFinite(quota?.countUsed) && quota.countUsed + 1 > quota.countLimit) {
      openGoPremium(
        texts?.transferCountExceeded
      )
      return
    }
    navigate("/")
  }
  const closeNewShare = () => {
    setShowNewShare(false)
    refresh()
  }

  const refresh = () => {
    setRefreshKey((k) => k + 1)
    setSelected(new Set())
  }

  // Realtime — any owner-relevant share event published by the Account-level
  // bridge triggers a list refresh. Cheap because DataTable already
  // debounces requestFields changes.
  useEffect(() => {
    const events = [
      "transfer.share.created",
      "transfer.share.status",
      "transfer.share.download",
      "transfer.share.scan",
      "transfer.share.access",
      "transfer.share.deleted",
    ]
    const tokens = events.map((e) => PubSub.subscribe(e, () => refresh()))
    return () => tokens.forEach((t) => PubSub.unsubscribe(t))
  }, [])

  const apiMethod = useCallback(async (params) => {
    const result = await ShareAPI.ListFileShares(params)
    if (result?.data) {
      setCurrentData(result.data)
      setSelected(new Set())
      setDrawerShare((prev) => {
        if (!prev) return null
        const updated = result.data.find((d) => d.orderID === prev.orderID)
        return updated || prev
      })
    }
    return result
  }, [])

  const toggleSelect = (orderID) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(orderID)) next.delete(orderID)
      else next.add(orderID)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === currentData.length && currentData.length > 0) {
      setSelected(new Set())
    } else {
      setSelected(new Set(currentData.map((d) => d.orderID)))
    }
  }

  const getShareUrl = (token) => {
    let domain = window.location.origin
    if (~config.PLATFORM_API_URL.indexOf("localhost:")) {
      domain = "http://192.168.1.55:3000"
    }
    return `${domain}/s/${token}`
  }

  const handleCopy = async (token) => {
    const url = getShareUrl(token)
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      try {
        const ta = document.createElement("textarea")
        ta.value = url
        ta.style.cssText = "position:fixed;opacity:0;pointer-events:none"
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand("copy")
        document.body.removeChild(ta)
      } catch {}
    }
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
    showToast({
      position: TOAST_POSITIONS.TOP_CENTER,
      variant: "success",
      duration: 3000,
      enableDismissalTracking: false,
      styleOverrides: { progressContainer: "hidden" },
      message: texts?.linkCopied || texts?.copied,
    })
  }

  const handleDeleteSingle = (share) => setConfirmDelete({ share })

  const handleDeleteSelected = () => {
    if (!selected.size) return
    setConfirmDelete("selected")
  }

  const executeDelete = async () => {
    setIsDeleting(true)
    try {
      if (confirmDelete === "selected") {
        await ShareAPI.DeleteManyFileShares([...selected])
      } else {
        const { share } = confirmDelete
        await ShareAPI.DeleteFileShare(share.orderID)
        if (drawerShare?.orderID === share.orderID) setDrawerShare(null)
      }
      setConfirmDelete(null)
      refresh()
    } catch {
      showMessageDialog({ title: texts?.error, message: texts?.failedDelete })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExtend = async (retentionDays) => {
    if (!extendModal || !retentionDays) return

    setExtending(true)
    try {
      const result = await ShareAPI.ExtendFileShare(extendModal.share.orderID, retentionDays)
      if (result.error) throw new Error(result.error)
      if (drawerShare?.orderID === extendModal.share.orderID) {
        setDrawerShare((prev) =>
          prev
            ? {
                ...prev,
                expires_at: result.expires_at,
                grace_expires_at: result.grace_expires_at,
                retention_days: result.retention_days,
                extends_left: result.extends_left,
                extend_count: result.extend_count,
                recipients_sent: 0,
                status: "completed",
              }
            : null,
        )
      }
      setExtendModal(null)
      refresh()
    } catch (err) {
      if (err.message === 'feature_locked') { openGoPremium(); return }
      const msg = err.message === 'extend_limit_reached'
        ? (texts?.extendLimitMessage)
        : err.message || texts?.failedUpdateEndDate
      showMessageDialog({ title: texts?.error, message: msg })
    } finally {
      setExtending(false)
    }
  }

  const handleSetLimit = async (limit) => {
    if (!drawerShare) return
    setLimitSaving(true)
    try {
      const result = await ShareAPI.SetDownloadLimit(drawerShare.orderID, limit)
      if (result.error) throw new Error(result.error)
      setDrawerShare((prev) =>
        prev ? { ...prev, download_limit: result.download_limit } : prev,
      )
      setLimitModal(false)
    } catch (err) {
      showMessageDialog({ title: texts?.error, message: err.message || texts?.failedUpdateDownloadLimit })
    } finally {
      setLimitSaving(false)
    }
  }

  const allSelected = currentData.length > 0 && selected.size === currentData.length
  const someSelected = selected.size > 0

  const renders = [
    {
      id: "orderID",
      title: (
        <Checkbox variant="transfer" isOn={allSelected} onChange={toggleSelectAll} />
      ),
      headerClassName: "w-[40px]",
      render: (orderID) => (
        <Checkbox variant="transfer" isOn={selected.has(orderID)} onChange={() => toggleSelect(orderID)} />
      ),
    },
    {
      id: "file_name",
      title: texts?.title,
      headerClassName: "min-w-[200px]",
      render: (file_name, item) => {
        const timeRemaining = formatTimeRemaining(item)
        const fileCount = item.file_count ?? (item.files?.length || 1)
        return (
          <div className="flex items-center gap-4 min-w-0">
            {item.share_token && item.status !== 'expired' && (
              <Button
                variant="secondary_s"
                onClick={() => handleCopy(item.share_token)}
                title={texts?.getLink}
                className="px-2"
              >
                {copiedToken === item.share_token ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-primary" />
                    <span className="text-primary">{texts?.copied}</span>
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    <span>{texts?.getLink}</span>
                  </>
                )}
              </Button>
            )}

            <div className="flex flex-col gap-1 min-w-0">
              <button
                onClick={() => setDrawerShare(item)}
                className="font-medium text-sm overflow-hidden w-full text-left text-link hover:underline transition-colors leading-snug flex items-center gap-2"
              >
                <span className="truncate">{item.title || (fileCount > 1 ? "" : file_name)}</span>
                {item.scan_pending && (
                  <span className="text-[10px] uppercase tracking-wide bg-warning/10 text-warning rounded-full px-2 py-0.5 inline-flex items-center gap-1 shrink-0">
                    {texts?.scanning}
                  </span>
                )}
                {item.direction === 'received' && (
                  <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary rounded-full px-2 py-0.5 shrink-0">
                    {texts?.received}
                  </span>
                )}
              </button>

              {item.title && fileCount === 1 && (
                <span className="text-xs text-text_secondary truncate">{file_name}</span>
              )}

              {item.direction === 'received' && item.sender_email && (
                <span className="text-xs text-text_secondary truncate">
                  {texts?.from} <span className="text-text">{item.sender_name || item.sender_email}</span>
                  {item.receiver_link_alias ? ` · ${item.receiver_link_alias}` : ""}
                </span>
              )}

              <div className="flex items-center gap-2 text-xs text-text_secondary">
                {item.file_size > 0 && <span>
                                        {formatBytes(item.file_size)}
                                      </span>}

                {timeRemaining && (
                  <span>
                    <strong>{timeRemaining.value}</strong> {timeRemaining.suffix}
                  </span>
                )}

                <span>
                  {fileCount} {fileCount === 1 ? (texts?.file) : (texts?.files)}
                </span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      id: "view_count",
      title: texts?.views,
      headerClassName: "w-[90px]",
      render: (view_count) => (
        <span className="flex items-center gap-1 text-sm">
          <EyeIcon className="h-4 w-4 text-text_secondary flex-shrink-0" />
          {formatNumber(view_count)}
        </span>
      ),
    },
    {
      id: "download_count",
      title: texts?.downloads,
      headerClassName: "w-[110px]",
      render: (download_count) => (
        <span className="flex items-center gap-1 text-sm">
          <ArrowDownTrayIcon className="h-4 w-4 text-text_secondary flex-shrink-0" />
          {formatNumber(download_count)}
        </span>
      ),
    },
    {
      id: "created",
      title: texts?.date,
      headerClassName: "w-[100px]",
      render: (created) => {
        if (!created) return null
        const d = new Date(created)
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        const yesterday = new Date(now)
        yesterday.setDate(now.getDate() - 1)
        const isYesterday = d.toDateString() === yesterday.toDateString()
        const dateLabel = isToday
          ? texts?.today
          : isYesterday
            ? texts?.yesterday
            : d.toLocaleDateString()
        return (
          <Tooltip text={formatRelativeTime(created)} position="top">
            <div className="flex flex-col text-xs">
              <span>{dateLabel}</span>
              <span className="text-text_secondary">
                {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </Tooltip>
        )
      },
    },
    {
      id: "status",
      title: texts?.status,
      headerClassName: "w-[110px]",
      render: (status) => <StatusBadge status={status} />,
    },
    {
      id: "actions",
      title: "",
      headerClassName: "w-[80px]",
      render: (_, item) => (
        <div className="flex items-center gap-1">
          {false && (item.status === "active" ||
            item.status === "completed" ||
            item.status === "grace") && (
            <button
              onClick={() => openExtend(item)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title={texts?.extend}
            >
              <CalendarDaysIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => handleDeleteSingle(item)}
            disabled={isDeleting}
            className="p-1.5 rounded-lg text-gray-400 hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
            title={texts?.delete}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ]

  const headerControls = (
    <div className="flex items-center gap-2">
      <DropdownSelect
        options={STATUS_OPTIONS}
        value={statusFilter}
        valueField="value"
        placeholder={texts?.allStatuses}
        multiSelection
        showChevron
        fixedWidth={200}
        panelWidth={200}
        zIndex={400}
        onChange={(selected) => {
          setStatusFilter(selected)
          setRefreshKey((k) => k + 1)
          setSelected(new Set())
        }}
      />
      {someSelected && (
        <Button variant="secondary_red_s" onClick={handleDeleteSelected} disabled={isDeleting}>
          {isDeleting ? (
            <Spinner className="w-[16px] h-[16px]" />
          ) : (
            <span className="flex items-center gap-1">
              <TrashIcon className="h-4 w-4" />
              {texts?.delete} ({selected.size})
            </span>
          )}
        </Button>
      )}
      <Button onClick={openNewShare} variant="primary_s">
        {texts?.newShare}
      </Button>
    </div>
  )

  return (
    <>
      {/* Desktop table — hidden on mobile but stays mounted so apiMethod still runs */}
      <div className="hidden lg:block bg-paper border border-bordered rounded-2xl overflow-hidden p-4 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18),0_10px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-black/5">
        <DataTable
          apiMethod={apiMethod}
          renders={renders}
          searchField="file_name orderID"
          searchObject={{ file_name: "" }}
          searchPlaceholder={texts?.searchPlaceholder}
          onSearchTermChange={setSearchTerm}
          headerControls={headerControls}
          tableHeader={<div className="h-2" />}
          refreshKey={refreshKey}
          requestFields={{ statusFilter: statusFilter.map((s) => s.value) }}
          itemHeight="h-auto min-h-[80px]"
          preferredHeight="55vh"
          styleOverrides={{
            searchInput: "ml-0 w-[340px] lg:w-[480px]",
            headerRow: "text-left text-text text-sm md:text-base xl:text-[18px] font-semibold align-top bg-neutral_1/40 border-b border-bordered",
            getHeaderCellStyle: (headerClassName) =>
              `px-4 py-4 ${headerClassName || ""}`,
            getCellStyle: (itemClassName) =>
              `px-4 py-4 border-b border-neutral_1/50 ${itemClassName || ""}`,
          }}
          noData={
            searchTerm.trim() ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <ShareIcon className="h-12 w-12 text-gray-300" />
                <Typography variant="h4" className="text-gray-500">
                  {texts?.noResults}
                </Typography>
              </div>
            ) : statusFilter.length > 0 ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <ShareIcon className="h-12 w-12 text-gray-300" />
                <Typography variant="h4" className="text-gray-500">
                  {texts?.noMatchingShares}
                </Typography>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 py-8">
                <ShareIcon className="h-12 w-12 text-gray-300" />
                <Typography variant="h4" className="text-gray-500">
                  {texts?.noSharedFiles}
                </Typography>
                <Button onClick={openNewShare} variant="primary_s">
                  {texts?.shareAFile}
                </Button>
              </div>
            )
          }
        />
      </div>

      {/* Mobile compact list */}
      <div className="lg:hidden flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <Typography className="text-text text-xl font-semibold">
            {texts?.sharedFiles}
          </Typography>
          <Button onClick={openNewShare} variant="primary">
            {texts?.newShare}
          </Button>
        </div>
        <MobileFileList
          auth={auth}
          statusFilter={statusFilter.map((s) => s.value)}
          refreshKey={refreshKey}
          onInfo={(item) => setDrawerShare(item)}
        />
      </div>

      <ShareDrawer
        share={drawerShare}
        open={!!drawerShare}
        onClose={() => setDrawerShare(null)}
        auth={auth}
        isDeleting={isDeleting}
        copiedToken={copiedToken}
        onCopy={handleCopy}
        getShareUrl={getShareUrl}
        onDelete={handleDeleteSingle}
        onExtend={openExtend}
        onOpenLimitModal={(currentLimit) => {
          setLimitValue(currentLimit != null ? String(currentLimit) : "")
          setLimitModal(true)
        }}
        onShareUpdated={(updated) => {
          // Drawer edits stay in sync with the table so closing + reopening
          // the row reflects the latest values (notes / theme / access /
          // password etc.) without waiting for a list refetch.
          setDrawerShare(updated)
          setCurrentData((prev) => prev.map((row) =>
            row.orderID === updated.orderID ? { ...row, ...updated } : row,
          ))
        }}
        onRefresh={refresh}
      />

      <DeleteModal
        confirmDelete={confirmDelete}
        selectedCount={selected.size}
        isDeleting={isDeleting}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={executeDelete}
      />

      <ExtendModal
        share={extendModal?.share}
        isOpen={!!extendModal}
        onClose={() => setExtendModal(null)}
        extending={extending}
        onExtend={handleExtend}
      />

      <DownloadLimitModal
        isOpen={limitModal}
        onClose={() => setLimitModal(false)}
        currentLimit={drawerShare?.download_limit}
        limitValue={limitValue}
        setLimitValue={setLimitValue}
        limitSaving={limitSaving}
        onSave={handleSetLimit}
        onRemove={() => handleSetLimit(null)}
      />

      {showNewShare && (
        <Modal
          variant="pulse_fullscreen"
          isOpen={true}
          onClose={closeNewShare}
          allowCloseBtn={true}
          title={texts?.newShareTitle}
        >
          <FileShareController auth={auth} hideInfo />
        </Modal>
      )}
    </>
  )
}
